import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { toast } from "@/components/feedback/Toast"
import { Button } from "@/components/ui/Button"
import { ConfirmDialog } from "@/components/ui/ConfirmDialog"
import { AlarmsFilters } from "@/features/alarms/components/AlarmsFilters"
import { AlarmsTable } from "@/features/alarms/components/AlarmsTable"
import { useMarkAllAlarmsRead } from "@/features/alarms/hooks/useMarkAllAlarmsRead"
import { useUnreadAlarmsCount } from "@/features/alarms/hooks/useUnreadAlarmsCount"
import type { AlarmListParams } from "@/features/alarms/schemas"

// 2026-04-24 — `?id=<guid>` deep-link (alarm detay drawer).
const searchSchema = z.object({ id: z.string().optional() })

export const Route = createFileRoute("/_authed/alarms")({
  component: AlarmsPage,
  validateSearch: (raw) => searchSchema.parse(raw),
})

function AlarmsPage() {
  const { t } = useTranslation("alarms")
  const { t: tCommon } = useTranslation("common")
  const { id } = Route.useSearch()
  const [filter, setFilter] = useState<AlarmListParams>({ acknowledged: false })
  const [confirmMarkAll, setConfirmMarkAll] = useState(false)
  const markAll = useMarkAllAlarmsRead()
  const { data: unread } = useUnreadAlarmsCount()

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex items-start justify-between gap-4 border-b border-ok-border px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t("title")}</h1>
          <p className="text-xs text-ok-foreground-muted">{t("subtitle")}</p>
        </div>
        {/* Sayfa girişindeki otomatik işaretleme yalnız yüklenen sayfayı kapatıyor;
            birikim büyükse rozet hiç düşmüyor. Bu buton kapsamın tamamını kapatır. */}
        <Button
          intent="secondary"
          size="sm"
          disabled={markAll.isPending || unread === 0}
          onClick={() => setConfirmMarkAll(true)}
        >
          {t("action.markAllRead")}
        </Button>
      </div>
      <AlarmsFilters filter={filter} onChange={setFilter} />
      <AlarmsTable filter={filter} initialSelectedId={id ?? null} />

      <ConfirmDialog
        open={confirmMarkAll}
        onOpenChange={setConfirmMarkAll}
        title={t("markAll.title")}
        description={t("markAll.description")}
        confirmLabel={t("action.markAllRead")}
        cancelLabel={tCommon("actions.cancel")}
        closeLabel={tCommon("actions.close")}
        loading={markAll.isPending}
        onConfirm={async () => {
          try {
            const { updated } = await markAll.mutateAsync()
            toast.success(
              updated > 0 ? t("toast.allMarked", { count: updated }) : t("toast.allMarkedNone"),
            )
            setConfirmMarkAll(false)
          } catch (e) {
            toast.error(t("toast.markAllFailed"), (e as Error).message)
          }
        }}
      >
        <p className="text-xs text-ok-foreground-muted">{t("markAll.hint")}</p>
      </ConfirmDialog>
    </div>
  )
}
